import { useMemo } from "react";

import {
  ALL_CHART_TYPES,
  DEFAULT_AXIS_FONT_WEIGHT,
  DEFAULT_FONT_SIZE,
  IS_STACKED_BAR_HORIZONTAL_SCREENSHOT
} from "constants/chart.constants";
import { ALL_WELL_STATUSES, WELL_STATUS_CURRENT_TITLE } from "constants/charts.enums";
import { THEME_FONT_FAMILY } from "constants/style.constants";
import {
  resolveWellStatusSelection,
  wellStatusDisplayName,
  wellStatusOverTimeChartTitle
} from "entities/charts/eva/requests/wellStatus";
import { firstSwatchedSegmentEnd } from "entities/charts/eva/translators/mpcAxisLegend";
import { useDashboardScreenshotStore } from "stores/dashboardScreenshot/useDashboardScreenshotStore";
import { MultiProductFeature } from "types/charts/feature";
import {
  isRigFracActivityRequestChartType,
  isRigFracRecentActivityRequestChartType,
  isWellStatusCurrentRequestChartType,
  isWellStatusOverTimeRequestChartType
} from "types/charts/requests";
import {
  getSortedXAxisIndices,
  getSortedYAxisIndices
} from "utils/chart/getSortedAxisIndices";

import { AxisProductSegment, IChartScreenshotSettings, IChartType } from "models/chart";
import { WidgetComponentType } from "models/screenshot";

import {
  useScreenshotContext,
  useScreenshotPresetContext
} from "components/screenshot/hooks";

import { CHART_WIDGET_TYPES } from "./chartWidgetTypes";

const getChartType = (chartTypeId: number): IChartType => {
  if (chartTypeId === null) return null;
  const chartType = Object.values(ALL_CHART_TYPES).find(
    (chartType) => chartType.id === chartTypeId
  );

  return chartType;
};

// For single-product MPC x-axes the unit lives in the swatch suffix; strip it from
// the editable default so the user only sees the product name (e.g. "Cum Gas").
function getMpcXAxisEditableTitle(title: string | undefined): string | undefined {
  if (!title) return title;
  const withoutUnit = title.replace(/\s*\([^)]*\)\s*$/, "").trim();
  return withoutUnit || title;
}

// Returns the correct editable default title for an MPC x-axis accordion input.
// - Legend OFF: full axis title.
// - Single-product: segment label only (unit lives in the swatch suffix).
// - Combined axis: lead text = everything in axis.title up to the end of the
//   first segment label that gets a swatch (else the first segment), preserving any
//   prefix like "Cum".
function getMpcXAxisDefaultTitle(
  axis: { title?: string; productSegments?: AxisProductSegment[] } | undefined,
  showProductLegend: boolean,
  multiProduct?: Partial<MultiProductFeature>
): string | undefined {
  const title = axis?.title;
  const segments = axis?.productSegments;
  if (!showProductLegend) return title;
  if (!segments?.length) return getMpcXAxisEditableTitle(title);
  if (segments.length === 1) return segments[0].label ?? getMpcXAxisEditableTitle(title);
  const swatchedEnd = title ? firstSwatchedSegmentEnd(title, segments, multiProduct) : -1;
  if (swatchedEnd >= 0) return title.slice(0, swatchedEnd);
  const firstLabel = segments[0].label;
  if (firstLabel && title) {
    const idx = title.indexOf(firstLabel);
    if (idx >= 0) return title.slice(0, idx + firstLabel.length);
  }
  return getMpcXAxisEditableTitle(title);
}

const getTitleText = (
  useLegacyTitles: boolean | undefined,
  useUppercaseTitles: boolean | undefined,
  legacyTitle: string | undefined,
  title: string | undefined
): string | undefined => {
  if (useLegacyTitles) {
    return legacyTitle?.toUpperCase();
  }

  if (useUppercaseTitles) {
    return title?.toUpperCase() ?? undefined;
  }

  return title ?? undefined;
};

function useChartScreenshotCalculatedDefaults(): IChartScreenshotSettings {
  // hooks
  const { widget, widgetState, settings } = useScreenshotContext();
  const { selectedPreset: preset } = useScreenshotPresetContext();
  const dashboardWidgets = useDashboardScreenshotStore((s) => s.activeWidgets);
  const dashboardHasChart = dashboardWidgets.some((w) => CHART_WIDGET_TYPES.has(w.type));

  const chartType = getChartType(widgetState?.request?.chartSetting?.chartType);
  const numericChartType = widgetState?.request?.chartSetting?.chartType;

  const isBoxPlot = chartType === ALL_CHART_TYPES.BoxPlot;
  const isMosaic = chartType === ALL_CHART_TYPES.Mosaic;
  const isCrossPlot = chartType === ALL_CHART_TYPES.CrossPlot;
  const isMaterialBalanceTime = chartType === ALL_CHART_TYPES.MaterialBalanceTime;
  const isStackedBar = chartType === ALL_CHART_TYPES.StackedBar;
  const isProbit = chartType === ALL_CHART_TYPES.Probit;
  const isRateDate = chartType === ALL_CHART_TYPES.RateDate;
  const isCagrChart = chartType === ALL_CHART_TYPES.CAGR;
  const isBaseDeclineRateChart = chartType === ALL_CHART_TYPES.BaseDeclineRate;
  const isTotalRateDate = chartType === ALL_CHART_TYPES.TotalRateDate;
  const isWellContribution = chartType === ALL_CHART_TYPES.WellContribution;
  const isRateTime = chartType === ALL_CHART_TYPES.RateTime;
  const isCumTime = chartType === ALL_CHART_TYPES.CumTime;
  // StackedBarHorizontal shares StackedBar's wire chartType, so `isStackedBar`
  // above is true for both — distinguish via which axis carries the category type.
  const isStackedBarHorizontal = IS_STACKED_BAR_HORIZONTAL_SCREENSHOT(
    numericChartType,
    widgetState?.options?.yAxis
  );
  const showXMajorLines = !(
    isRateDate ||
    isCagrChart ||
    isBaseDeclineRateChart ||
    isTotalRateDate ||
    isCrossPlot ||
    isBoxPlot ||
    isWellContribution ||
    (isStackedBar && !isStackedBarHorizontal)
  );

  // Handle X-axis for both array and single object cases (xAxis is an array in multi-product mode)
  let showXMinorLines = isProbit || isMaterialBalanceTime;
  if (widgetState?.options?.xAxis) {
    if (Array.isArray(widgetState.options.xAxis)) {
      showXMinorLines = showXMinorLines || widgetState.options.xAxis[0]?.type === "log";
    } else {
      showXMinorLines = showXMinorLines || widgetState.options.xAxis.type === "log";
    }
  }

  // Handle Y-axis for both array and single object cases
  let showYMinorLines = isMaterialBalanceTime;
  if (widgetState?.options?.yAxis) {
    if (Array.isArray(widgetState.options.yAxis)) {
      showYMinorLines = showYMinorLines || widgetState.options.yAxis[0]?.type === "log";
    } else {
      showYMinorLines = showYMinorLines || widgetState.options.yAxis.type === "log";
    }
  }

  let xAxisTitle = widgetState?.response?.layout?.xAxis?.title;
  //rate and cum time both have dynamic titles depending on range
  if ((isBoxPlot && widgetState?.request?.showScatter) || isRateTime || isCumTime) {
    xAxisTitle = widgetState?.options?.xAxis?.name;
  }
  // Mosaic has two X-axes: xAxis[0] at bottom ("Vintage Year") and xAxis[1] at top ("Well Count").
  // getSortedXAxisIndices puts top first, so getChartScreenshotOptions applies xAxisTitleText to
  // the top axis. Derive the default from that same sorted-first axis so the screenshot preserves
  // the live "Well Count" label instead of overwriting it with the bottom axis title.
  if (
    isMosaic &&
    Array.isArray(widgetState?.options?.xAxis) &&
    widgetState.options.xAxis.length > 1
  ) {
    const mosaicSortedIndices = getSortedXAxisIndices(widgetState.options.xAxis);
    xAxisTitle = widgetState.options.xAxis[mosaicSortedIndices[0]]?.name ?? xAxisTitle;
  }
  // The rig/frac charts (EVA-76) carry a lean request with a string chartType
  // discriminator instead of a numeric chartSetting.chartType. Match it with the
  // shared request-side guards (NOT the options-side EvaChart enum) so the
  // literals live in one place beside the request types.
  const rigFracChartType = widgetState?.request?.chartType;
  // Activity vs Time is the only rig/frac chart with a secondary (right) y-axis
  // — Frac Crew Count. It's a non-multi-product dual-axis chart, so it needs the
  // secondary y-axis title control the multi-product path exposes.
  const isRigFracActivityChart = isRigFracActivityRequestChartType(rigFracChartType);
  // Recent Activity draws HORIZONTAL bars — its translator flips the backend's
  // vertical-bar layout, so the category (group) titles live on the y-axis and
  // the metric on the x-axis. The screenshot title defaults must flip to match,
  // or the labels read swapped (metric over the operator axis) (EVA-76 QA).
  const isRigFracRecentChart = isRigFracRecentActivityRequestChartType(rigFracChartType);
  const isRigFracChart = isRigFracActivityChart || isRigFracRecentChart;
  // The Well Status charts are lean-request charts too, and share every
  // trait that matters here: no legacyTitle on the response, and — for Current
  // Well Status — the same horizontal flip as Recent Activity.
  const isWellStatusOverTimeChart =
    isWellStatusOverTimeRequestChartType(rigFracChartType);
  const isWellStatusCurrentChart = isWellStatusCurrentRequestChartType(rigFracChartType);
  const isWellStatusChart = isWellStatusOverTimeChart || isWellStatusCurrentChart;
  // Charts whose translator flips the backend's vertical-bar layout onto
  // horizontal bars: the category (group) titles live on the y-axis and the
  // metric on the x-axis, so the screenshot title defaults must flip to match or
  // the labels read swapped (metric over the group axis). StackedBarHorizontal
  // does the exact same flip (see translators/stackedBarHorizontal.ts) against
  // the same unflipped backend response shape StackedBar gets, so it slots into
  // this same title-default branch with no further changes needed there.
  const isHorizontalBarChart =
    isRigFracRecentChart || isWellStatusCurrentChart || isStackedBarHorizontal;
  // The Well Status charts DISPLAY client-built titles — "Well Status vs Time
  // (Monthly)" and the readable status name ("Producing") — while the response
  // carries the server's raw forms ("PROD Count vs Time", "PROD Count"). Seeding
  // the panel from the response therefore pre-filled text that did not match the
  // chart underneath it. Rebuild them from the REQUEST with the same
  // pure helpers the translators use, so the two cannot drift.
  const wellStatusRequest = widgetState?.request as Maybe<{
    status?: string;
    timeScale?: string;
    allStatuses?: boolean;
    customRange?: { from?: string; to?: string };
    focus?: { title?: string };
  }>;
  // Stacked "All Statuses" mode measures a total across statuses, so no single
  // status name fits — it echoes the SELECTION instead. Resolved through the same
  // helper as the chart's own axis (useWellStatusChartOptions) so the seeded text
  // cannot drift from what is drawn.
  // `widgetState.request` is the SAVED request until the controller's first echo, so
  // over time it is resolved as the chart does, not left naming a stale label.
  const wellStatusMetricTitle = wellStatusDisplayName(
    wellStatusRequest?.allStatuses
      ? ALL_WELL_STATUSES
      : isWellStatusOverTimeChart
      ? resolveWellStatusSelection(wellStatusRequest?.status, false)
      : wellStatusRequest?.status
  );
  // The secondary (Frac Crew Count) axis is only rendered when the overlay line
  // exists — the translator sets `yAxis2.show = hasLine`. Read that live flag off
  // the rendered options (yAxis[1]) so we don't seed a persisted title for an
  // axis the chart isn't drawing (matches the controls panel's gate).
  const rigFracSecondaryYAxis = Array.isArray(widgetState?.options?.yAxis)
    ? (widgetState.options.yAxis as Array<{ show?: boolean }>)[1]
    : undefined;
  const rigFracSecondaryYAxisVisible =
    rigFracSecondaryYAxis != null && rigFracSecondaryYAxis.show !== false;

  // useLegacyTitles is not supported in multi-product mode, and must NOT affect
  // the rig/frac or Well Status charts: "legacy titles" is a wells-only concept
  // and those lean responses carry no legacyTitle, so applying it returns
  // undefined — blanking the title override and, because getTitleText returns
  // early on the legacy branch, SUPPRESSING THE UPPERCASE TOGGLE entirely
  // (EVA-76 for rig/frac, product for Well Status: "buttons that don't work
  // like use uppercase titles"). Force it off so legacy is a clean no-op.
  const useLegacyTitles =
    widgetState?.multiProductActive || isRigFracChart || isWellStatusChart
      ? false
      : !!settings?.useLegacyTitles;

  const sortedYAxisIndices =
    widgetState?.multiProductActive && Array.isArray(widgetState?.options?.yAxis)
      ? getSortedYAxisIndices(widgetState.options.yAxis)
      : [];

  const sortedXAxisIndices =
    widgetState?.multiProductActive && Array.isArray(widgetState?.options?.xAxis)
      ? getSortedXAxisIndices(widgetState.options.xAxis)
      : [];

  return useMemo(() => {
    const isChart =
      widget?.widgetComponentType === WidgetComponentType.Chart ||
      widget?.widgetComponentType === WidgetComponentType.ChartBeta;
    const isDashboardWithChart =
      widget?.widgetComponentType === WidgetComponentType.Dashboard && dashboardHasChart;

    return !isChart && !isDashboardWithChart
      ? {}
      : {
          axisFontSize: preset?.axisFontSize,
          titleFontSize: preset?.titleFontSize,
          axisLabelFontSize: preset?.axisLabelFontSize,
          titleText: getTitleText(
            useLegacyTitles,
            !!settings?.useUppercaseTitles,
            widgetState?.response?.legacyTitle,
            // Status Over Time captions itself with the granularity it is drawn
            // at, which is a REQUEST property the response knows nothing about.
            isWellStatusOverTimeChart
              ? wellStatusOverTimeChartTitle(
                  wellStatusRequest?.timeScale,
                  wellStatusRequest?.customRange
                )
              : // Current Well Status captions itself with the chart's own name,
              // while the response's title is the server's raw "<abbrev> Count
              // — Current". Same constant the chart draws.
              isWellStatusCurrentChart
              ? WELL_STATUS_CURRENT_TITLE
              : widgetState?.response?.title
          ),
          showAxisTitle: true,
          xAxisTitleText: isHorizontalBarChart
            ? // horizontal flip: the x-axis is the METRIC (value) axis
              isWellStatusCurrentChart
              ? wellStatusMetricTitle
              : widgetState?.response?.layout?.yAxis?.title
            : widgetState?.multiProductActive
            ? Array.isArray(widgetState?.options?.xAxis)
              ? getMpcXAxisDefaultTitle(
                  widgetState?.response?.layout?.xAxis2?.[sortedXAxisIndices[0]],
                  settings?.showProductLegend !== false,
                  widgetState?.multiProduct
                )
              : xAxisTitle // single-object xAxis (e.g. Rate Time in MPC) — use the already-computed name
            : useLegacyTitles && !isBoxPlot && !isRateTime && !isCumTime && !isMosaic //rate and cum time do not have legacy titles for x axis; mosaic uses top-axis name
            ? widgetState?.response?.layout?.xAxis?.legacyTitle
            : xAxisTitle,
          yAxisTitleText: isHorizontalBarChart
            ? // horizontal flip: the y-axis is the GROUP (category) axis. The Well
              // Status response names that axis with the RAW focus property
              // ("Header.Operator_Short"), while the chart draws the focus's
              // display name — which rides in on the request. Recent Activity
              // shares this branch and keeps the label the response sends it.
              // An EMPTY focus title must fall through to the response's name,
              // so this coalesces on truthiness like the chart's own translator.
              (isWellStatusCurrentChart ? wellStatusRequest?.focus?.title : undefined) ||
              widgetState?.response?.layout?.xAxis?.title
            : widgetState?.multiProductActive
            ? widgetState?.response?.layout?.yAxis2?.[sortedYAxisIndices[0]]?.title
            : useLegacyTitles
            ? widgetState?.response?.layout?.yAxis?.legacyTitle
            : // Over Time's value axis is named for the STATUS being counted
            // ("Producing"), not the server's "<abbrev> Count".
            isWellStatusOverTimeChart
            ? wellStatusMetricTitle
            : widgetState?.response?.layout?.yAxis?.title,
          // Activity vs Time: seed the secondary (right) y-axis title input with
          // the backend's Frac Crew Count label so the new control pre-fills —
          // but only when that axis is actually drawn, so a title doesn't
          // persist for a hidden axis (no overlay line).
          ...(isRigFracActivityChart &&
            rigFracSecondaryYAxisVisible && {
              yAxis2TitleText: widgetState?.response?.layout?.yAxis2?.[0]?.title
            }),
          ...(widgetState?.multiProductActive && {
            ...Object.fromEntries(
              sortedXAxisIndices
                .slice(1)
                .map((axisIdx, i) => [
                  `xAxis${i + 2}TitleText`,
                  getMpcXAxisDefaultTitle(
                    widgetState?.response?.layout?.xAxis2?.[axisIdx],
                    settings?.showProductLegend !== false,
                    widgetState?.multiProduct
                  )
                ])
            ),
            ...Object.fromEntries(
              sortedYAxisIndices
                .slice(1)
                .map((axisIdx, i) => [
                  `yAxis${i + 2}TitleText`,
                  widgetState?.response?.layout?.yAxis2?.[axisIdx]?.title
                ])
            ),
            mergeSameUnitAxes: widgetState?.request?.combineSameUnitAxis ?? false,
            showProductLegend: !!widgetState?.multiProduct?.properties?.showProductLegend
          }),
          axisFontWeight: DEFAULT_AXIS_FONT_WEIGHT,
          showAxisLabels: true,
          legendShowCounts: true,
          legendOpacity: 1,
          legendShowTypeWellRescat: true,
          fontFamily: THEME_FONT_FAMILY,
          // Rig/frac gridline defaults track each chart's actual axes so the
          // (now-functional) Major toggles show the true state: Activity vs Time
          // hides its category x-axis gridlines; Recent Activity's category axis
          // is the y-axis, so it hides those and keeps its value x-axis on.
          // Well Status vs Time has the same axis shape as Activity vs Time (a
          // category x, a value y), and Current Well Status the same as Recent
          // Activity (category y, value x) — so their gridline defaults follow
          // the same rule: hide the CATEGORY axis's lines, keep the value axis's.
          gridShowXLines:
            isRigFracActivityChart || isWellStatusOverTimeChart ? false : showXMajorLines,
          gridShowXMinorLines: showXMinorLines,
          gridShowYLines: isHorizontalBarChart ? false : true,
          gridShowYMinorLines: showYMinorLines,
          legendVisible: true,
          legendBorderVisible: true,
          legendTitleSize: preset?.legendTitleSize ?? DEFAULT_FONT_SIZE,
          legendFontSize: preset?.legendFontSize ?? DEFAULT_FONT_SIZE,
          applyAbbreviations: false,
          // EVA-127 "Simplify Custom Field Names" — default OFF.
          simplifyCustomFieldNames: false,
          // EVA-430: the box plot's Data Range subtitle shows unless turned off.
          ...(isBoxPlot && { showDataRangeSubtitle: true }),
          isWrappingText: true,
          // No X-Label Rotation control for the horizontal chart (its x-axis
          // is numeric, not category), so the default is undefined like any
          // other non-rotatable chart, not the StackedBar family's 0.
          xLabelRotation:
            isBoxPlot || (isStackedBar && !isStackedBarHorizontal) || isMosaic
              ? 0
              : undefined
        };
  }, [
    widget?.widgetComponentType,
    dashboardHasChart,
    preset?.axisFontSize,
    preset?.titleFontSize,
    preset?.axisLabelFontSize,
    preset?.legendTitleSize,
    preset?.legendFontSize,
    widgetState?.response?.title,
    widgetState?.response?.layout?.xAxis?.title,
    widgetState?.response?.layout?.yAxis?.title,
    widgetState?.multiProductActive,
    widgetState?.multiProduct,
    widgetState?.request?.combineSameUnitAxis,
    widgetState?.response?.layout?.xAxis2,
    widgetState?.response?.layout?.yAxis2,
    showXMajorLines,
    showXMinorLines,
    settings?.showProductLegend,
    settings?.useLegacyTitles,
    settings?.useUppercaseTitles,
    widgetState?.options?.yAxis,
    widgetState?.options?.xAxis,
    numericChartType,
    // The rig/frac + Well Status title flips and secondary-title defaults key off
    // the lean request's string chartType, which isn't derivable from
    // numericChartType — depend on it directly so switching charts within one
    // widget recomputes even if the titles happen to coincide.
    widgetState?.request?.chartType,
    // Well Status titles are rebuilt from the request, so they must recompute
    // when the user changes status / time scale / All Statuses mid-capture.
    wellStatusRequest?.status,
    wellStatusRequest?.timeScale,
    wellStatusRequest?.allStatuses,
    // The custom range belongs in that set too: with timeScale already Custom,
    // a DIFFERENT range can resolve to a different granularity (Weekly → Monthly),
    // and the caption names it. Scalars, not the object, so the memo isn't
    // rebuilt by a new-but-equal range.
    wellStatusRequest?.customRange?.from,
    wellStatusRequest?.customRange?.to,
    // The group axis is named after the focus, so switching focus mid-capture
    // must reseed it. exhaustive-deps is off in this repo, so nothing else
    // catches a missing dep here.
    wellStatusRequest?.focus?.title
  ]);
}

export default useChartScreenshotCalculatedDefaults;
